"use client";

import {
  Badge,
  Button,
  type CellContext,
  type ColumnDef,
  cn,
  DataGrid,
  DataGridBase,
  DataGridColumnHeader,
  DataGridContainer,
  DataGridTable,
  Label,
  type PaginationState,
  ToolbarGroup,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { CdvType } from "@enums";
import type { IHubCdv } from "@models";
import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";

const DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: true,
  height: "full",
  headerSticky: true,
} as const;

function labelForCdvType(
  typeKey: string,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const tierLabel = t("hub.cdv_manager.type_tier", { defaultValue: "Tier" });
  const cloudLabel = t("hub.cdv_manager.type_cloud", {
    defaultValue: "Cloud",
  });
  if (typeKey === CdvType.Tier) return tierLabel;
  if (typeKey === CdvType.Cloud) return cloudLabel;
  return (
    typeKey || t("hub.cdv_manager.type_unknown", { defaultValue: "Other" })
  );
}

type CdvTypeGroupRow = {
  id: string;
  typeKey: string;
  displayLabel: string;
  searchBlob: string;
  count: number;
  items: IHubCdv[];
};

type CdvManagerGroupsGridProps = {
  cdvs: IHubCdv[];
  isLoading: boolean;
  hasCdvManage: boolean;
  onCreateClick: () => void;
  onEdit: (row: IHubCdv) => void;
  onRequestDelete: (row: IHubCdv) => void;
  onSearchChange?: (value: string) => void;
  pagination?: PaginationState;
  onPaginationChange?: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
};

export function CdvManagerGroupsGrid({
  cdvs,
  isLoading,
  hasCdvManage,
  onCreateClick,
  onEdit,
  onRequestDelete,
  onSearchChange,
  pagination,
  onPaginationChange,
}: CdvManagerGroupsGridProps) {
  const { t } = useTranslation();

  const afterActions = useMemo(
    () =>
      hasCdvManage ? (
        <Button type="button" variant="outline" onClick={onCreateClick}>
          <Plus className="mr-2 size-4" />
          {t("hub.cdv_manager.create", { defaultValue: "New CDV" })}
        </Button>
      ) : null,
    [hasCdvManage, onCreateClick, t],
  );

  const sortedFlat = useMemo(() => {
    return [...cdvs].sort((a, b) => {
      const byType = (a.type ?? "").localeCompare(b.type ?? "");
      if (byType !== 0) return byType;
      return (a.label || a.name).localeCompare(b.label || b.name);
    });
  }, [cdvs]);

  const groupRows = useMemo((): CdvTypeGroupRow[] => {
    const map = new Map<string, IHubCdv[]>();
    for (const c of sortedFlat) {
      const key = c.type?.trim() || "__empty__";
      const bucket = map.get(key) ?? [];
      bucket.push(c);
      map.set(key, bucket);
    }
    return [...map.entries()]
      .map(([typeKey, items]) => {
        const sortedItems = [...items].sort((a, b) =>
          (a.label || a.name).localeCompare(b.label || b.name),
        );
        const displayLabel =
          typeKey === "__empty__"
            ? t("hub.cdv_manager.type_unknown", { defaultValue: "Other" })
            : labelForCdvType(typeKey, t);
        const searchBlob = [
          displayLabel,
          typeKey,
          ...sortedItems.flatMap((c) => [
            c.label ?? "",
            c.name ?? "",
            c.type ?? "",
            labelForCdvType(c.type ?? "", t),
          ]),
        ]
          .join(" ")
          .toLowerCase();
        return {
          id: `group-${typeKey}`,
          typeKey,
          displayLabel,
          searchBlob,
          count: sortedItems.length,
          items: sortedItems,
        };
      })
      .sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }, [sortedFlat, t]);

  const headerTextClassName =
    "text-foreground px-1 py-0.5 font-semibold text-sm";

  const innerColumns = useMemo<ColumnDef<IHubCdv, unknown>[]>(
    () => [
      {
        accessorKey: "name",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.cdv_manager.column_name", { defaultValue: "Name" })}
            className={headerTextClassName}
          />
        ),
        cell: (info: CellContext<IHubCdv, unknown>) => (
          <Label className="block min-w-0 truncate font-normal">
            {(info.getValue() as string) ?? ""}
          </Label>
        ),
      },
      {
        accessorKey: "label",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.cdv_manager.column_label", {
              defaultValue: "Label",
            })}
            className={headerTextClassName}
          />
        ),
        cell: (info: CellContext<IHubCdv, unknown>) => (
          <Label className="block min-w-0 truncate font-normal">
            {(info.getValue() as string) ?? ""}
          </Label>
        ),
      },
      {
        accessorKey: "isDefault",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.cdv_manager.column_default", {
              defaultValue: "Default",
            })}
            className={headerTextClassName}
          />
        ),
        cell: (info: CellContext<IHubCdv, unknown>) =>
          info.row.original.isDefault ? (
            <Badge variant="secondary" size="sm">
              {t("hub.cdv_manager.yes", { defaultValue: "Yes" })}
            </Badge>
          ) : (
            <span className="text-muted-foreground">
              {t("hub.cdv_manager.no", { defaultValue: "No" })}
            </span>
          ),
      },
      {
        id: "action",
        header: t("hub.cdv_manager.actions", { defaultValue: "Actions" }),
        enableSorting: false,
        cell: (info: CellContext<IHubCdv, unknown>) =>
          hasCdvManage ? (
            <ToolbarGroup className="gap-2">
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(info.row.original);
                }}
                aria-label={t("hub.cdv_manager.edit", {
                  defaultValue: "Edit",
                })}>
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestDelete(info.row.original);
                }}
                disabled={info.row.original.id == null}
                aria-label={t("hub.cdv_manager.delete", {
                  defaultValue: "Delete",
                })}>
                <Trash2 className="size-4 text-destructive" />
              </Button>
            </ToolbarGroup>
          ) : null,
      },
    ],
    [hasCdvManage, t, headerTextClassName, onEdit, onRequestDelete],
  );

  const outerColumns = useMemo<ColumnDef<CdvTypeGroupRow, unknown>[]>(
    () => [
      {
        id: "typeGroup",
        accessorKey: "displayLabel",
        filterFn: (row, _columnId, filterValue) => {
          const q = String(filterValue ?? "")
            .trim()
            .toLowerCase();
          if (!q) return true;
          return row.original.searchBlob.includes(q);
        },
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.cdv_manager.column_type", {
              defaultValue: "Type",
            })}
            className={headerTextClassName}
          />
        ),
        cell: ({ row, getValue }) => (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              aria-expanded={row.getIsExpanded()}
              aria-label={
                row.getIsExpanded()
                  ? t("hub.cdv_manager.collapse_group", {
                      defaultValue: "Collapse group",
                    })
                  : t("hub.cdv_manager.expand_group", {
                      defaultValue: "Expand group",
                    })
              }>
              <ChevronRight
                className={cn(
                  "size-4 transition-transform",
                  row.getIsExpanded() && "rotate-90",
                )}
              />
            </Button>
            <span className="font-medium">{getValue() as string}</span>
            <Badge variant="secondary" className="shrink-0 font-normal">
              {t("hub.cdv_manager.group_count", {
                count: row.original.count,
                defaultValue: "{{count}} items",
              })}
            </Badge>
          </div>
        ),
        meta: {
          expandedContent: (group: CdvTypeGroupRow) => (
            <div
              className={cn(
                "border-t border-border bg-muted/20 p-2",
                "flex flex-col",
              )}>
              <DataGridBase
                key={group.id}
                data={group.items}
                columns={innerColumns}
                recordCount={group.items.length}
                tableLayout={{ headerSticky: true }}
                columnResizeMode="onChange">
                <DataGridContainer
                  border={true}
                  className="rounded-md max-h-96 overflow-y-auto bg-background">
                  <DataGridTable />
                </DataGridContainer>
              </DataGridBase>
            </div>
          ),
        },
      },
    ],
    [t, headerTextClassName, innerColumns],
  );

  return (
    <DataGrid<CdvTypeGroupRow>
      data={groupRows}
      columns={outerColumns}
      recordCount={groupRows.length}
      tableLayout={DATA_GRID_LAYOUT}
      paginationPlacement="toolbar"
      paginationConfig={{ showRowsPerPage: true }}
      manualPagination
      pageCount={Math.ceil(
        (groupRows.length ?? 0) / (pagination?.pageSize ?? 10),
      )}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      withExport
      withSearch={{
        searchKey: "typeGroup",
        placeholder: t("hub.cdv_manager.search_placeholder", {
          defaultValue: "Search by label…",
        }),
        onChange: onSearchChange,
      }}
      afterActions={afterActions}
      loading={isLoading}
      loadingMessage={t("hub.cdv_manager.loading", {
        defaultValue: "Loading CDVs…",
      })}
      emptyMessage={t("hub.cdv_manager.empty", {
        defaultValue: "No configuration values yet.",
      })}
    />
  );
}
